import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Cropper from "react-easy-crop";
import Button from "../../Components/Button/Button";
import TextField from "../../Components/TextField/TextField";
import { useToast } from "../../Contexts/ToastContext/ToastContext";
import api from "../../services/api";
import "./Products.css";

const EPC_MIN_LENGTH = 4;
const EPC_MAX_LENGTH = 24;
const EPC_HEX_CHARS = "0123456789ABCDEF";
const EPC_HEX_REGEX = /^[0-9A-Fa-f]*$/;

const Products = () => {
  const { showToast } = useToast();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [expandedEpcs, setExpandedEpcs] = useState(new Set());
  const [showModal, setShowModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [selectedImageFile, setSelectedImageFile] = useState(null);
  const [imagePreviewUrl, setImagePreviewUrl] = useState("");
  const [croppedPreviewUrl, setCroppedPreviewUrl] = useState("");
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [aspectRatio, setAspectRatio] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [removeImage, setRemoveImage] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const [saving, setSaving] = useState(false);
  const fileInputRef = useRef(null);
  const [form, setForm] = useState({
    epc: "",
    name: "",
    price: "",
    serialNumber: "",      // optional catalogue / serial number — informational only
    defaultExpiryDays: "", // empty string means "no default"; stored on the product type
  });

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const data = await api.getProducts();
      setProducts(data.products || []);
    } catch (error) {
      showToast(error.message || "Failed to fetch products", 3000, true, "danger");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProducts();
  }, []);

  const filteredProducts = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return products;
    return products.filter((p) =>
      (p.name || "").toLowerCase().includes(q) ||
      (p.epc || "").toLowerCase().includes(q)
    );
  }, [products, searchTerm]);

  const resetForm = () => {
    if (imagePreviewUrl?.startsWith("blob:")) {
      URL.revokeObjectURL(imagePreviewUrl);
    }
    if (croppedPreviewUrl?.startsWith("blob:")) {
      URL.revokeObjectURL(croppedPreviewUrl);
    }
    setForm({ epc: "", name: "", price: "", serialNumber: "", defaultExpiryDays: "" });
    setEditingProduct(null);
    setSelectedImageFile(null);
    setImagePreviewUrl("");
    setCroppedPreviewUrl("");
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setAspectRatio(1);
    setCroppedAreaPixels(null);
    setRemoveImage(false);
    setIsDragOver(false);
  };

  useEffect(() => {
    return () => {
      if (imagePreviewUrl?.startsWith("blob:")) {
        URL.revokeObjectURL(imagePreviewUrl);
      }
      if (croppedPreviewUrl?.startsWith("blob:")) {
        URL.revokeObjectURL(croppedPreviewUrl);
      }
    };
  }, [imagePreviewUrl, croppedPreviewUrl]);

  const openCreateModal = () => {
    resetForm();
    setShowModal(true);
  };

  const openEditModal = (product) => {
    setEditingProduct(product);
    setForm({
      epc: product.epc || "",
      name: product.name || "",
      price: product.price?.toString() || "",
      serialNumber: product.serialNumber || "",
      defaultExpiryDays:
        typeof product.defaultExpiryDays === "number" ? String(product.defaultExpiryDays) : "",
    });
    setImagePreviewUrl(product.productImgLink || "");
    setCroppedPreviewUrl("");
    setSelectedImageFile(null);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setAspectRatio(1);
    setCroppedAreaPixels(null);
    setRemoveImage(false);
    setShowModal(true);
  };

  const applySelectedImage = (file) => {
    if (!file.type.startsWith("image/")) {
      showToast("Please select a valid image file", 2500, true, "danger");
      return;
    }
    if (imagePreviewUrl?.startsWith("blob:")) URL.revokeObjectURL(imagePreviewUrl);
    if (croppedPreviewUrl?.startsWith("blob:")) URL.revokeObjectURL(croppedPreviewUrl);
    setSelectedImageFile(file);
    setRemoveImage(false);
    const objectUrl = URL.createObjectURL(file);
    setImagePreviewUrl(objectUrl);
    setCroppedPreviewUrl("");
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setAspectRatio(1);
    setCroppedAreaPixels(null);
  };

  const handleImageSelect = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    applySelectedImage(file);
  };

  const cropImageByArea = useCallback(async (file, areaPixels) => {
    if (!areaPixels) return file;
    const imageUrl = URL.createObjectURL(file);
    try {
      const img = await new Promise((resolve, reject) => {
        const el = new Image();
        el.onload = () => resolve(el);
        el.onerror = reject;
        el.src = imageUrl;
      });
      const sx = Math.max(0, Math.floor(areaPixels.x));
      const sy = Math.max(0, Math.floor(areaPixels.y));
      const sw = Math.max(1, Math.floor(areaPixels.width));
      const sh = Math.max(1, Math.floor(areaPixels.height));
      const canvas = document.createElement("canvas");
      canvas.width = sw;
      canvas.height = sh;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, sx, sy, sw, sh, 0, 0, sw, sh);
      const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/jpeg", 0.92));
      if (!blob) return file;
      return new File([blob], file.name.replace(/\.[^/.]+$/, "") + "-cropped.jpg", {
        type: "image/jpeg",
      });
    } finally {
      URL.revokeObjectURL(imageUrl);
    }
  }, []);

  useEffect(() => {
    if (!selectedImageFile || !croppedAreaPixels) {
      if (croppedPreviewUrl?.startsWith("blob:")) {
        URL.revokeObjectURL(croppedPreviewUrl);
      }
      setCroppedPreviewUrl("");
      return;
    }
    let cancelled = false;
    const timeout = setTimeout(async () => {
      try {
        const cropped = await cropImageByArea(selectedImageFile, croppedAreaPixels);
        if (cancelled) return;
        const previewUrl = URL.createObjectURL(cropped);
        setCroppedPreviewUrl((prev) => {
          if (prev?.startsWith("blob:")) URL.revokeObjectURL(prev);
          return previewUrl;
        });
      } catch {
        if (!cancelled) setCroppedPreviewUrl("");
      }
    }, 120);
    return () => {
      cancelled = true;
      clearTimeout(timeout);
    };
  }, [selectedImageFile, croppedAreaPixels, cropImageByArea]);

  const handleDropImage = (event) => {
    event.preventDefault();
    setIsDragOver(false);
    const file = event.dataTransfer?.files?.[0];
    if (file) applySelectedImage(file);
  };

  const generateRandomEpc = () => {
    // Length must be a multiple of 4 (4, 8, 12, 16, 20, 24)
    const validLengths = [4, 8, 12, 16, 20, 24].filter((l) => l >= EPC_MIN_LENGTH && l <= EPC_MAX_LENGTH);
    const targetLength = validLengths[Math.floor(Math.random() * validLengths.length)];
    let epc = "";
    for (let i = 0; i < targetLength; i++) {
      epc += EPC_HEX_CHARS[Math.floor(Math.random() * EPC_HEX_CHARS.length)];
    }
    return epc;
  };

  const handleGenerateEpc = () => {
    const existingEpcs = new Set(products.map((p) => (p.epc || "").toUpperCase()));
    // Allow keeping current EPC while editing the same product.
    if (editingProduct?.epc) existingEpcs.delete((editingProduct.epc || "").toUpperCase());

    for (let attempt = 0; attempt < 100; attempt++) {
      const candidate = generateRandomEpc();
      if (!existingEpcs.has(candidate)) {
        setForm((prev) => ({ ...prev, epc: candidate }));
        return;
      }
    }

    showToast("Could not generate a unique EPC. Try again.", 2500, true, "danger");
  };

  const toggleExpandEpc = (productId) => {
    setExpandedEpcs((prev) => {
      const next = new Set(prev);
      if (next.has(productId)) next.delete(productId);
      else next.add(productId);
      return next;
    });
  };

  const truncateEpc = (epc, maxLength = 12) => {
    if (!epc || epc.length <= maxLength) return epc;
    return `${epc.substring(0, maxLength)}...`;
  };

  const handleSave = async () => {
    const epc = form.epc.trim().toUpperCase();
    if (!EPC_HEX_REGEX.test(epc)) {
      showToast("Product EPC must be hexadecimal (0-9, A-F)", 2500, true, "danger");
      return;
    }
    if (epc.length < EPC_MIN_LENGTH || epc.length > EPC_MAX_LENGTH) {
      showToast(`Product EPC must be ${EPC_MIN_LENGTH}-${EPC_MAX_LENGTH} hex characters`, 2500, true, "danger");
      return;
    }
    if (!form.name.trim()) {
      showToast("Product name is required", 2500, true, "danger");
      return;
    }
    const price = parseFloat(form.price);
    if (Number.isNaN(price) || price < 0) {
      showToast("Price must be a valid number >= 0", 2500, true, "danger");
      return;
    }

    try {
      setSaving(true);
      let productImgLink = editingProduct?.productImgLink || "";
      if (removeImage) productImgLink = "";
      if (selectedImageFile) {
        const uploadFile = await cropImageByArea(selectedImageFile, croppedAreaPixels);
        const upload = await api.uploadProductImage(uploadFile);
        productImgLink = upload.imageUrl || "";
      }

      // defaultExpiryDays: empty string → null (clear); otherwise non-negative int.
      const trimmedDefault = String(form.defaultExpiryDays || "").trim();
      let defaultExpiryDays;
      if (trimmedDefault === "") {
        defaultExpiryDays = null;
      } else {
        const n = Math.round(Number(trimmedDefault));
        if (!Number.isFinite(n) || n < 0) {
          showToast("Default expiry (days) must be 0 or a positive whole number", 2500, true, "danger");
          setSaving(false);
          return;
        }
        defaultExpiryDays = n;
      }

      const payload = editingProduct
        ? {
            name: form.name.trim(),
            productImgLink,
            price,
            defaultExpiryDays,
            serialNumber: form.serialNumber.trim(),
          }
        : {
            epc,
            name: form.name.trim(),
            productImgLink,
            price,
            defaultExpiryDays,
            serialNumber: form.serialNumber.trim(),
          };
      if (editingProduct) {
        await api.updateProduct(editingProduct._id, payload);
        showToast("Product type updated", 2500, true, "success");
      } else {
        await api.createProduct(payload);
        showToast("Product type created", 2500, true, "success");
      }
      setShowModal(false);
      resetForm();
      fetchProducts();
    } catch (error) {
      showToast(error.message || "Failed to save product type", 3000, true, "danger");
    } finally {
      setSaving(false);
    }
  };

  const openDeleteConfirm = (product) => {
    setDeleteTarget(product);
    setShowDeleteConfirm(true);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.deleteProduct(deleteTarget._id);
      showToast("Product type deleted", 2500, true, "success");
      setShowDeleteConfirm(false);
      setDeleteTarget(null);
      fetchProducts();
    } catch (error) {
      showToast(error.message || "Failed to delete product type", 3000, true, "danger");
    }
  };

  return (
    <div className="products-page">
      <div className="dashboard-content">
        <div className="dashboard-header">
          <div>
            <h1 className="dashboard-title">Product Types</h1>
            <p className="dashboard-subtitle">Manage catalog product types used in the system</p>
          </div>
          <Button value="Add Product Type" iconLeft="fa-plus" onClick={openCreateModal} variant="primary" />
        </div>

        <div className="filters-section">
          <div className="filters-grid">
            <TextField
              placeholder="Search by product name or EPC..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        <div className="table-section">
          <div className="section-header">
            <h2 className="section-title">
              Product List
              <span className="results-count" style={{ marginLeft: 8 }}>
                ({filteredProducts.length})
              </span>
            </h2>
          </div>

          <div className="table-container">
            {loading ? (
              <div className="loading-state">
                <i className="fa fa-spinner fa-spin" style={{ fontSize: "32px", color: "var(--text-tertiary)" }} />
                <p>Loading products...</p>
              </div>
            ) : filteredProducts.length === 0 ? (
              <div className="empty-state">
                <i className="fa fa-cubes empty-state-icon" />
                <p>No product types found</p>
              </div>
            ) : (
              <table className="tags-table">
                <thead>
                  <tr>
                    <th>EPC</th>
                    <th>Name</th>
                    <th>Image</th>
                    <th>Price</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredProducts.map((product) => (
                    <tr key={product._id}>
                      <td>
                        <span
                          className="tag-badge"
                          onClick={() => toggleExpandEpc(product._id)}
                          title={expandedEpcs.has(product._id) ? "Click to collapse" : "Click to expand"}
                          style={{
                            cursor: "pointer",
                            userSelect: "none",
                            maxWidth: expandedEpcs.has(product._id) ? "unset" : "150px",
                            display: "inline-block",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            verticalAlign: "bottom",
                          }}
                        >
                          {expandedEpcs.has(product._id) ? product.epc : truncateEpc(product.epc)}
                        </span>
                      </td>
                      <td>
                        <span className="epc-cell fw-bold">{product.name}</span>
                      </td>
                      <td>
                        {product.productImgLink ? (
                          <img
                            src={product.productImgLink}
                            alt={product.name}
                            style={{ width: 42, height: 42, objectFit: "cover", borderRadius: 8, border: "1px solid var(--border-primary)" }}
                          />
                        ) : (
                          <span style={{ color: "var(--text-tertiary)" }}>—</span>
                        )}
                      </td>
                      <td>
                        <span className="tag-badge">${Number(product.price || 0).toFixed(2)}</span>
                      </td>
                      <td>
                        <div style={{ display: "flex", gap: 8 }}>
                          <button className="action-btn edit-btn" onClick={() => openEditModal(product)} title="Edit">
                            <i className="fa fa-pencil" />
                          </button>
                          <button className="action-btn delete-btn" onClick={() => openDeleteConfirm(product)} title="Delete">
                            <i className="fa fa-trash" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>

      {showModal && (
        <div className="modal-overlay" onClick={() => { setShowModal(false); resetForm(); }}>
          <div className="modal-container tag-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header-wrapper">
              <div className="modal-icon-circle tag-icon-bg">
                <i className="fa fa-cube" />
              </div>
              <div className="modal-title-section">
                <span className="modal-title">{editingProduct ? "Edit Product Type" : "Add Product Type"}</span>
                <span className="modal-subtitle">Name, image upload and price</span>
              </div>
              <div className="modal-close-btn" onClick={() => { setShowModal(false); resetForm(); }}>
                <i className="fa fa-times" />
              </div>
            </div>

            <div className="modal-body-wrapper">
              <div className="form-field">
                <div className="form-label-wrapper">
                  <span className="form-label">Product EPC (hex, {EPC_MIN_LENGTH}-{EPC_MAX_LENGTH} chars)</span>
                  <span className="form-label-required">*</span>
                  <button
                    type="button"
                    onClick={handleGenerateEpc}
                    disabled={!!editingProduct}
                    style={{
                      marginLeft: "auto",
                      border: "1px solid var(--border-primary)",
                      backgroundColor: editingProduct ? "var(--bg-tertiary)" : "var(--bg-secondary)",
                      color: "var(--text-primary)",
                      borderRadius: "8px",
                      padding: "4px 10px",
                      fontSize: "12px",
                      fontWeight: 600,
                      cursor: editingProduct ? "not-allowed" : "pointer",
                      opacity: editingProduct ? 0.6 : 1,
                    }}
                    title="Generate random unique EPC"
                  >
                    <i className="fa fa-random" style={{ marginRight: 6 }} />
                    Generate
                  </button>
                </div>
                <TextField
                  placeholder="e.g. 300833B2DDD9"
                  value={form.epc}
                  onChange={(e) => {
                    const v = e.target.value.toUpperCase().replace(/[^0-9A-F]/g, "");
                    setForm({ ...form, epc: v });
                  }}
                  maxLength={EPC_MAX_LENGTH}
                  disabled={!!editingProduct}
                />
              </div>
              <div className="form-field">
                <div className="form-label-wrapper">
                  <span className="form-label">Product Name</span>
                  <span className="form-label-required">*</span>
                </div>
                <TextField
                  placeholder="e.g. Laptop"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                />
              </div>
              <div className="form-field">
                <div className="form-label-wrapper">
                  <span className="form-label">Serial Number</span>
                  <span className="form-label-hint">optional</span>
                </div>
                <TextField
                  placeholder="e.g. SN-2024-00123"
                  value={form.serialNumber}
                  onChange={(e) => setForm({ ...form, serialNumber: e.target.value })}
                />
              </div>
              <div className="form-field">
                <div className="form-label-wrapper">
                  <span className="form-label">Product Image</span>
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleImageSelect}
                  style={{ display: "none" }}
                />
                <div
                  className={`product-upload-dropzone ${isDragOver ? "drag-over" : ""}`}
                  onDragOver={(e) => { e.preventDefault(); setIsDragOver(true); }}
                  onDragLeave={() => setIsDragOver(false)}
                  onDrop={handleDropImage}
                  onClick={() => fileInputRef.current?.click()}
                >
                  <div className="product-upload-icon">
                    <i className="fa fa-cloud-upload" />
                  </div>
                  <div className="product-upload-text">
                    <strong>Drag & drop image here</strong>
                    <span>or click to browse files</span>
                  </div>
                </div>
                {imagePreviewUrl && !removeImage && (
                  <div className="product-upload-preview-wrap">
                    {selectedImageFile && (
                      <div className="product-cropper-editor">
                        <div className="product-cropper-shell">
                          <Cropper
                            image={imagePreviewUrl}
                            crop={crop}
                            zoom={zoom}
                            aspect={aspectRatio}
                            onCropChange={setCrop}
                            onZoomChange={setZoom}
                            onCropComplete={(_, areaPixels) => setCroppedAreaPixels(areaPixels)}
                          />
                        </div>
                        <div className="product-cropper-controls">
                          <label htmlFor="crop-zoom">
                            Zoom
                            <input
                              id="crop-zoom"
                              type="range"
                              min={1}
                              max={3}
                              step={0.05}
                              value={zoom}
                              onChange={(e) => setZoom(Number(e.target.value))}
                            />
                          </label>
                          <label htmlFor="crop-aspect">
                            Aspect
                            <select
                              id="crop-aspect"
                              value={aspectRatio}
                              onChange={(e) => setAspectRatio(Number(e.target.value))}
                            >
                              <option value={1}>1:1 (Square)</option>
                              <option value={4 / 3}>4:3</option>
                              <option value={16 / 9}>16:9</option>
                              <option value={3 / 4}>3:4</option>
                            </select>
                          </label>
                        </div>
                      </div>
                    )}
                    {croppedPreviewUrl && (
                      <div className="product-upload-preview-card">
                        <p>Cropped</p>
                        <img src={croppedPreviewUrl} alt="Cropped preview" className="product-upload-preview-img" />
                      </div>
                    )}
                    {!selectedImageFile && (
                      <div className="product-upload-preview-card">
                        <p>Current</p>
                        <img src={imagePreviewUrl} alt="Current preview" className="product-upload-preview-img" />
                      </div>
                    )}
                  </div>
                )}
                <div className="product-upload-actions">
                  <button
                    type="button"
                    onClick={() => {
                      if (imagePreviewUrl?.startsWith("blob:")) URL.revokeObjectURL(imagePreviewUrl);
                      if (croppedPreviewUrl?.startsWith("blob:")) URL.revokeObjectURL(croppedPreviewUrl);
                      setRemoveImage(true);
                      setSelectedImageFile(null);
                      setImagePreviewUrl("");
                      setCroppedPreviewUrl("");
                      setCrop({ x: 0, y: 0 });
                      setZoom(1);
                      setAspectRatio(1);
                      setCroppedAreaPixels(null);
                    }}
                    className="product-upload-remove"
                    disabled={!imagePreviewUrl}
                  >
                    <i className="fa fa-trash" />
                    Remove Image
                  </button>
                </div>
                {!imagePreviewUrl && (
                  <p style={{ margin: "8px 0 0", fontSize: 12, color: "var(--text-tertiary)" }}>
                    Upload an image, then drag and zoom to crop exactly what you want.
                  </p>
                )}
              </div>
              <div className="form-field">
                <div className="form-label-wrapper">
                  <span className="form-label">Price</span>
                  <span className="form-label-required">*</span>
                </div>
                <TextField
                  type="number"
                  min="0"
                  step="0.01"
                  placeholder="0.00"
                  value={form.price}
                  onChange={(e) => setForm({ ...form, price: e.target.value })}
                />
              </div>

              {/* Default expiry — the value stamped on each NEW tag of this
                  product type. Changing it later doesn't touch existing tags. */}
              <div className="form-field">
                <div className="form-label-wrapper">
                  <span className="form-label">Default expiry (days)</span>
                  <span className="form-label-hint">optional · auto-fills endDate on new tags</span>
                </div>
                <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                  <TextField
                    type="number"
                    min="0"
                    step="1"
                    placeholder="e.g. 365"
                    value={form.defaultExpiryDays}
                    onChange={(e) => setForm({ ...form, defaultExpiryDays: e.target.value })}
                    style={{ flex: 1, minWidth: 120 }}
                  />
                  <div
                    style={{
                      display: "inline-flex",
                      gap: 4,
                      background: "var(--bg-tertiary)",
                      padding: 3,
                      borderRadius: "var(--radius-md)",
                      border: "1px solid var(--border-primary)",
                    }}
                  >
                    {[
                      { label: "30 d",  days: 30 },
                      { label: "6 mo",  days: 180 },
                      { label: "1 y",   days: 365 },
                      { label: "2 y",   days: 730 },
                    ].map((opt) => (
                      <button
                        key={opt.days}
                        type="button"
                        onClick={() => setForm({ ...form, defaultExpiryDays: String(opt.days) })}
                        style={{
                          border: "none",
                          background: "transparent",
                          padding: "4px 9px",
                          borderRadius: "calc(var(--radius-md) - 2px)",
                          fontSize: 11,
                          fontWeight: "var(--weight-semibold)",
                          color: "var(--text-secondary)",
                          cursor: "pointer",
                        }}
                      >
                        {opt.label}
                      </button>
                    ))}
                    {form.defaultExpiryDays && (
                      <button
                        type="button"
                        onClick={() => setForm({ ...form, defaultExpiryDays: "" })}
                        title="Clear"
                        style={{
                          border: "none",
                          background: "transparent",
                          padding: "4px 9px",
                          borderRadius: "calc(var(--radius-md) - 2px)",
                          fontSize: 11,
                          color: "var(--error, #DC2626)",
                          cursor: "pointer",
                        }}
                      >
                        <i className="fa fa-times" aria-hidden />
                      </button>
                    )}
                  </div>
                </div>
                <p style={{ margin: "6px 0 0", fontSize: 11.5, color: "var(--text-tertiary)" }}>
                  Every new tag of this product type will get an end date <strong>{form.defaultExpiryDays ? `${form.defaultExpiryDays} day${form.defaultExpiryDays === "1" ? "" : "s"}` : "—"}</strong> from creation.
                  Changing this later does not affect tags that already exist.
                </p>
              </div>
            </div>

            <div className="modal-footer-wrapper">
              <div className="modal-action-btn cancel-btn" onClick={() => { setShowModal(false); resetForm(); }}>
                <i className="fa fa-times" />
                <span>Cancel</span>
              </div>
              <div
                className="modal-action-btn primary-btn"
                onClick={saving ? undefined : handleSave}
                style={{ opacity: saving ? 0.7 : 1, cursor: saving ? "not-allowed" : "pointer" }}
              >
                <i className={`fa ${saving ? "fa-spinner fa-spin" : editingProduct ? "fa-save" : "fa-plus"}`} />
                <span>{saving ? "Saving..." : editingProduct ? "Save Changes" : "Add Product Type"}</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {showDeleteConfirm && (
        <div className="modal-overlay" onClick={() => { setShowDeleteConfirm(false); setDeleteTarget(null); }}>
          <div className="modal-container tag-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header-wrapper">
              <div className="modal-icon-circle" style={{ backgroundColor: "var(--error-subtle)", color: "var(--error)" }}>
                <i className="fa fa-trash" />
              </div>
              <div className="modal-title-section">
                <span className="modal-title">Delete Product Type</span>
                <span className="modal-subtitle">
                  This action cannot be undone
                </span>
              </div>
              <div className="modal-close-btn" onClick={() => { setShowDeleteConfirm(false); setDeleteTarget(null); }}>
                <i className="fa fa-times" />
              </div>
            </div>

            <div className="modal-body-wrapper">
              <p style={{ margin: 0, color: "var(--text-secondary)" }}>
                Are you sure you want to delete{" "}
                <strong style={{ color: "var(--text-primary)" }}>
                  {deleteTarget?.name || "this product type"}
                </strong>
                ?
              </p>
            </div>

            <div className="modal-footer-wrapper">
              <div className="modal-action-btn cancel-btn" onClick={() => { setShowDeleteConfirm(false); setDeleteTarget(null); }}>
                <i className="fa fa-times" />
                <span>Cancel</span>
              </div>
              <div className="modal-action-btn" style={{ backgroundColor: "var(--error)", color: "#fff" }} onClick={handleDelete}>
                <i className="fa fa-trash" />
                <span>Delete</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Products;
